import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import toast from 'react-hot-toast'
import { Plus, X, GripVertical, Printer, Edit, Trash2, Loader2, BookOpen, Link, Download, Upload, FileText } from 'lucide-react'
import HelpButton from '../components/HelpButton'
import { JobStatusModal } from '../components/JobStatusModal'
import { useJobStatus } from '../hooks/useJobStatus'
import { UnifiedCurriculumEditor } from '../components/UnifiedCurriculumEditor'
import { CurriculumPrintView } from '../components/CurriculumPrintView'
import { generatePDF, sanitizeFilename } from '../utils/pdfGenerator'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface Curriculum {
  id: number
  cid?: string
  name: string
  subject: string
  exam_type?: string
  updated_at?: string
}

interface SortableRowProps {
  curriculum: Curriculum
  onEdit: (id: number) => void
  onDelete: (id: number) => void
  onPrint: (id: number) => void
  onExport: (id: number) => void
  onEditCurriculum: (curriculum: Curriculum) => void
  isSelected: boolean
  onSelect: (id: number) => void
}

function SortableRow({ curriculum, onEdit, onDelete, onPrint, onExport, onEditCurriculum, isSelected, onSelect }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: curriculum.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <tr ref={setNodeRef} style={style} className={isDragging ? 'bg-gray-100' : ''}>
      <td className="px-2 py-4 text-center w-12">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(curriculum.id)}
          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
      </td>
      <td className="px-6 py-4 text-sm text-gray-700" style={{ maxWidth: '120px' }}>
        <div className="flex items-center gap-2">
          <button
            {...attributes}
            {...listeners}
            className="p-1 hover:bg-gray-200 rounded cursor-grab active:cursor-grabbing flex-shrink-0"
          >
            <GripVertical className="w-4 h-4 text-gray-400" />
          </button>
          <span className="break-words">{curriculum.cid || '-'}</span>
        </div>
      </td>
      <td className="px-6 py-4 text-sm font-medium text-gray-900" style={{ maxWidth: '250px' }}>
        <div className="break-words">{curriculum.name}</div>
      </td>
      <td className="px-6 py-4 text-sm text-gray-700" style={{ maxWidth: '150px' }}>
        <div className="break-words">{curriculum.subject}</div>
      </td>
      <td className="px-6 py-4 text-sm text-gray-700" style={{ maxWidth: '120px' }}>
        <div className="break-words">{curriculum.exam_type || '-'}</div>
      </td>
      <td className="px-6 py-4 text-right text-sm whitespace-nowrap">
        <div className="flex items-center gap-2 justify-end">
          <button
            onClick={() => onPrint(curriculum.id)}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title="Drucken"
          >
            <Printer className="w-4 h-4" />
          </button>
          <button
            onClick={() => onExport(curriculum.id)}
            className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
            title="Exportieren"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            onClick={() => onEdit(curriculum.id)}
            className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
            title="Bearbeiten"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(curriculum.id)}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Löschen"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  )
}

export default function Curricula() {
  const navigate = useNavigate()
  const [items, setItems] = useState<Curriculum[]>([])
  const [filteredItems, setFilteredItems] = useState<Curriculum[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [isReordering, setIsReordering] = useState(false)
  
  // Import/Export State
  const [importFile, setImportFile] = useState<File | null>(null)
  const [isImporting, setIsImporting] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  
  // Selection State
  const [selectedCurricula, setSelectedCurricula] = useState<number[]>([])
  const [showSelectionModal, setShowSelectionModal] = useState(false)
  
  // Job Modal State
  const [currentJobId, setCurrentJobId] = useState<string | null>(null)
  const [showJobModal, setShowJobModal] = useState(false)
  
  // Editor Modal State
  const [showUnifiedEditor, setShowUnifiedEditor] = useState(false)
  const [selectedCurriculum, setSelectedCurriculum] = useState<Curriculum | null>(null)
  
  // Print Modal State
  const [showPrintModal, setShowPrintModal] = useState(false)
  const [printData, setPrintData] = useState<{
    curriculum: Curriculum | null
    competencies: any[]
    contentFields: any[]
    mappings: any[]
  }>({
    curriculum: null,
    competencies: [],
    contentFields: [],
    mappings: []
  })
  
  // Form state for manual creation
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    exam_type: '',
    cid: ''
  })

  // Drag & Drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const load = async () => {
    try {
      const res = await api.get('/curricula')
      setItems(res.data.data || [])
      setFilteredItems(res.data.data || [])
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Fehler beim Laden')
    }
  }

  // Filter items based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredItems(items)
    } else {
      const filtered = items.filter(item => 
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.cid && item.cid.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.exam_type && item.exam_type.toLowerCase().includes(searchQuery.toLowerCase()))
      )
      setFilteredItems(filtered)
    }
  }, [searchQuery, items])

  useEffect(() => { load() }, [])

  const onImport = async () => {
    if (!file) return
    setIsUploading(true)
    
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('subjects', '') // Leer für automatische Erkennung
      fd.append('year_levels', '') // Leer für automatische Erkennung  
      fd.append('exam_type', '') // Leer für automatische Erkennung
      
      // Starte asynchronen Import
      const res = await api.post('/curricula-async/import-unified', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      
      if (res.data.success) {
        const jobId = res.data.data.jobId
        
        // Zeige Job-Status Modal
        setCurrentJobId(jobId)
        setShowJobModal(true)
        
        toast.success('Import gestartet! Sie können den Fortschritt im Modal verfolgen.')
        setFile(null)
        await load()
      }
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Import fehlgeschlagen')
    } finally {
      setIsUploading(false)
    }
  }

  // Job Modal Handlers
  const handleJobComplete = (result: any) => {
    console.log('Import abgeschlossen:', result)
    toast.success(`Import erfolgreich: ${result.insertedCurricula} Curricula, ${result.insertedCompetencies} Kompetenzen`)
    load() // Curricula-Liste aktualisieren
  }

  const handleJobModalClose = () => {
    setShowJobModal(false)
    setCurrentJobId(null)
  }

  // Editor Modal Handlers
  const handleEditCurriculum = (curriculum: Curriculum) => {
    setSelectedCurriculum(curriculum)
    setShowUnifiedEditor(true)
  }

  const handleEditorClose = () => {
    setShowUnifiedEditor(false)
    setSelectedCurriculum(null)
  }

  const handleEditorSave = () => {
    // Curricula-Liste aktualisieren nach Änderungen
    load()
  }

  const onCreateManual = async () => {
    if (!formData.name || !formData.subject) {
      toast.error('Name und Fach sind erforderlich')
      return
    }
    
    setIsCreating(true)
    try {
      const res = await api.post('/curricula', formData)
      toast.success('Curriculum erstellt')
      setShowCreateModal(false)
      setFormData({ name: '', subject: '', exam_type: '', cid: '' })
      await load()
      
      // Navigate to editor for the new curriculum
      if (res.data.data?.id) {
        navigate(`/curricula/${res.data.data.id}/edit`)
      }
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Erstellung fehlgeschlagen')
    } finally {
      setIsCreating(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (active.id !== over?.id) {
      const oldIndex = filteredItems.findIndex(item => item.id === active.id)
      const newIndex = filteredItems.findIndex(item => item.id === over?.id)

      const newFilteredItems = arrayMove(filteredItems, oldIndex, newIndex)
      setFilteredItems(newFilteredItems)

      // Send reorder request to backend
      try {
        setIsReordering(true)
        const curriculumIds = newFilteredItems.map(item => item.id)
        const response = await api.put('/curricula/reorder', { curriculumIds })
        toast.success('Reihenfolge aktualisiert')
        // Reload to get updated order from server
        await load()
      } catch (error: any) {
        console.error('Reorder error:', error)
        
        if (error.response?.status === 401 || error.response?.status === 403) {
          toast.error('Bitte melden Sie sich erneut an')
          // Redirect to login or refresh auth
          window.location.reload()
        } else if (error.response?.status === 400) {
          const errorMessage = error.response?.data?.error || 'Ungültige Anfrage'
          toast.error(`Fehler: ${errorMessage}`)
        } else {
          const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Unbekannter Fehler'
          toast.error(`Fehler beim Aktualisieren der Reihenfolge: ${errorMessage}`)
        }
        
        // Revert on error
        await load()
      } finally {
        setIsReordering(false)
      }
    }
  }

  const handleEdit = (id: number) => {
    navigate(`/curricula/${id}/edit`)
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Möchten Sie dieses Curriculum wirklich löschen?')) {
      return
    }

    try {
      await api.delete(`/curricula/${id}`)
      toast.success('Curriculum gelöscht')
      await load()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Löschen fehlgeschlagen')
    }
  }

  const handlePrint = async (id: number) => {
    try {
      // Load curriculum data for printing
      const [treeRes, contentFieldsRes, mappingsRes] = await Promise.all([
        api.get(`/curricula/${id}/tree`),
        api.get(`/curricula/${id}/content-fields`),
        api.get(`/curricula/${id}/mappings`)
      ])

      const curriculumData = treeRes.data.data
      const curriculum = {
        id: curriculumData.id,
        cid: curriculumData.cid,
        name: curriculumData.name,
        subject: curriculumData.subject,
        exam_type: curriculumData.exam_type,
        updated_at: curriculumData.updated_at
      }
      // Tree API returns competencies already in hierarchical structure
      const competencies = curriculumData.competencies || []
      const contentFields = contentFieldsRes.data.data || []
      const mappings = mappingsRes.data.data || []

      setPrintData({
        curriculum,
        competencies: competencies,
        contentFields,
        mappings
      })
      setShowPrintModal(true)
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Fehler beim Laden der Druckdaten')
    }
  }

  const handleGeneratePDF = async () => {
    if (!printData.curriculum) return;
    
    try {
      const filename = sanitizeFilename(printData.curriculum.name || 'curriculum');
      await generatePDF('curriculum-print-view', { filename });
      toast.success('PDF erfolgreich erstellt!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Fehler beim Erstellen der PDF');
    }
  };

  const handleExport = async (id: number) => {
    try {
      const response = await api.get(`/curricula/${id}/export`, {
        responseType: 'blob'
      })
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `curriculum-${id}.json`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      
      toast.success('Curriculum exportiert')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Export fehlgeschlagen')
    }
  }

  // Import/Export Functions
  const handleImport = async () => {
    if (!importFile) return
    
    setIsImporting(true)
    try {
      const formData = new FormData()
      formData.append('file', importFile)
      
      const response = await api.post('/curricula/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      
      if (response.data.success) {
        toast.success(`Import erfolgreich: ${response.data.importedCurricula} Curricula, ${response.data.importedCompetencies} Kompetenzen`)
        setImportFile(null)
        setShowImportModal(false)
        await load()
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Import fehlgeschlagen')
    } finally {
      setIsImporting(false)
    }
  }

  const handleExportAll = async () => {
    setIsExporting(true)
    try {
      const response = await api.get('/curricula/export/all', {
        responseType: 'blob'
      })
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `curricula-export-${new Date().toISOString().split('T')[0]}.json`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      
      toast.success('Alle Curricula exportiert')
      setShowExportModal(false)
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Export fehlgeschlagen')
    } finally {
      setIsExporting(false)
    }
  }

  // Selection Functions
  const handleSelectCurriculum = (id: number) => {
    setSelectedCurricula(prev => 
      prev.includes(id) 
        ? prev.filter(curriculumId => curriculumId !== id)
        : [...prev, id]
    )
  }

  const handleSelectAll = () => {
    if (selectedCurricula.length === filteredItems.length) {
      setSelectedCurricula([])
    } else {
      setSelectedCurricula(filteredItems.map(item => item.id))
    }
  }

  const handleExportSelected = async () => {
    if (selectedCurricula.length === 0) {
      toast.error('Bitte wählen Sie mindestens ein Curriculum aus')
      return
    }

    setIsExporting(true)
    try {
      const response = await api.post('/curricula/export/selected', 
        { curriculumIds: selectedCurricula },
        { responseType: 'blob' }
      )
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `curricula-selected-export-${new Date().toISOString().split('T')[0]}.json`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      
      toast.success(`${selectedCurricula.length} Curricula exportiert`)
      setShowSelectionModal(false)
      setSelectedCurricula([])
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Export fehlgeschlagen')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold">Curricula</h1>
          <HelpButton helpKey="curricula.overview" />
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            <Plus className="w-4 h-4" />
            Manuell erstellen
          </button>
          <HelpButton helpKey="curricula.create_manual" size="sm" />
          
          {/* PDF Import */}
          <input type="file" accept="application/pdf" onChange={e => setFile(e.target.files?.[0] || null)} />
          <button
            onClick={onImport}
            disabled={!file || isUploading}
            className="px-4 py-2 bg-primary-600 text-white rounded disabled:opacity-50 flex items-center gap-2"
          >
            {isUploading && <Loader2 className="w-4 h-4 animate-spin" />}
            {isUploading ? 'Importiere…' : 'PDF importieren'}
          </button>
          <HelpButton helpKey="curricula.pdf_import" size="sm" />
          
          {/* JSON Import */}
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            <Upload className="w-4 h-4" />
            JSON importieren
          </button>
          
          {/* Export All */}
          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
          >
            <Download className="w-4 h-4" />
            Alle exportieren
          </button>
          
          {/* Export Selected */}
          <button
            onClick={() => setShowSelectionModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            <Download className="w-4 h-4" />
            Ausgewählte exportieren
          </button>
        </div>
      </div>

      {/* Search Filter */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Curricula durchsuchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="mt-2 text-sm text-gray-600">
            {filteredItems.length} von {items.length} Curricula gefunden
          </p>
        )}
      </div>

      <div className="bg-white shadow rounded">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  <input
                    type="checkbox"
                    checked={selectedCurricula.length === filteredItems.length && filteredItems.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ maxWidth: '120px' }}>CID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ maxWidth: '250px' }}>Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ maxWidth: '150px' }}>Fach</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ maxWidth: '120px' }}>Prüfungsart</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <SortableContext items={filteredItems.map(item => item.id)} strategy={verticalListSortingStrategy}>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      {searchQuery ? 'Keine Curricula gefunden' : 'Noch keine Curricula vorhanden'}
                    </td>
                  </tr>
                ) : (
                  filteredItems.map(c => (
                    <SortableRow 
                      key={c.id} 
                      curriculum={c} 
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onPrint={handlePrint}
                      onExport={handleExport}
                      onEditCurriculum={handleEditCurriculum}
                      isSelected={selectedCurricula.includes(c.id)}
                      onSelect={handleSelectCurriculum}
                    />
                  ))
                )}
              </tbody>
            </SortableContext>
          </table>
        </DndContext>
        {isReordering && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
            <div className="text-sm text-gray-600">Aktualisiere Reihenfolge...</div>
          </div>
        )}
      </div>

      {/* Import Progress Modal */}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Neues Curriculum erstellen</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); onCreateManual(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="z.B. Mathematik ESA 2024"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fach *
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="z.B. Mathematik"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prüfungsart
                </label>
                <select
                  value={formData.exam_type}
                  onChange={(e) => handleInputChange('exam_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">(keine)</option>
                  <option value="ESA">ESA</option>
                  <option value="MSA">MSA</option>
                  <option value="FSA">FSA</option>
                  <option value="Abitur">Abitur</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CID (optional)
                </label>
                <input
                  type="text"
                  value={formData.cid}
                  onChange={(e) => handleInputChange('cid', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="z.B. KMK-MATH-ESA-2024"
                />
              </div>
              
              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  disabled={isCreating}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                >
                  {isCreating ? 'Erstelle...' : 'Erstellen'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Job Status Modal */}
      <JobStatusModal
        jobId={currentJobId}
        isOpen={showJobModal}
        onClose={handleJobModalClose}
        onComplete={handleJobComplete}
      />

      {/* Unified Curriculum Editor Modal */}
      <UnifiedCurriculumEditor
        curriculumId={selectedCurriculum?.id || 0}
        curriculumName={selectedCurriculum?.name || ''}
        isOpen={showUnifiedEditor}
        onClose={handleEditorClose}
        onSave={handleEditorSave}
      />

      {/* Print Modal */}
      {showPrintModal && printData.curriculum && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 no-print">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
            {/* Print Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  PDF Vorschau
                </h2>
                <p className="text-sm text-gray-600">{printData.curriculum.name}</p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleGeneratePDF}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  PDF erstellen
                </button>
                <button
                  onClick={() => setShowPrintModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Print Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              <div id="curriculum-print-view">
                <CurriculumPrintView
                  curriculum={printData.curriculum}
                  competencies={printData.competencies}
                  contentFields={printData.contentFields}
                  mappings={printData.mappings}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Curricula importieren</h2>
              <button
                onClick={() => setShowImportModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  JSON-Datei auswählen
                </label>
                <input
                  type="file"
                  accept=".json"
                  onChange={e => setImportFile(e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {importFile && (
                  <p className="mt-2 text-sm text-gray-600">
                    Ausgewählt: {importFile.name}
                  </p>
                )}
              </div>
              
              <div className="bg-blue-50 p-3 rounded-md">
                <p className="text-sm text-blue-800">
                  <strong>Hinweis:</strong> Die JSON-Datei sollte von einer anderen Lernrakete-Instanz exportiert worden sein. 
                  Existierende Curricula mit demselben Namen und Fach werden aktualisiert.
                </p>
              </div>
              
              <div className="flex gap-2 pt-4">
                <button
                  onClick={handleImport}
                  disabled={!importFile || isImporting}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isImporting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isImporting ? 'Importiere...' : 'Importieren'}
                </button>
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Alle Curricula exportieren</h2>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-purple-50 p-3 rounded-md">
                <p className="text-sm text-purple-800">
                  <strong>Export umfasst:</strong>
                </p>
                <ul className="mt-2 text-sm text-purple-700 list-disc list-inside">
                  <li>Alle Curricula mit Metadaten</li>
                  <li>Kompetenzen in hierarchischer Struktur</li>
                  <li>Inhaltsfelder und deren Eigenschaften</li>
                  <li>Zuordnungen zwischen Kompetenzen und Inhaltsfeldern</li>
                </ul>
              </div>
              
              <div className="bg-yellow-50 p-3 rounded-md">
                <p className="text-sm text-yellow-800">
                  <strong>Verwendung:</strong> Die exportierte JSON-Datei kann in eine andere Lernrakete-Instanz importiert werden.
                </p>
              </div>
              
              <div className="flex gap-2 pt-4">
                <button
                  onClick={handleExportAll}
                  disabled={isExporting}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isExporting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isExporting ? 'Exportiere...' : 'Exportieren'}
                </button>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Selection Modal */}
      {showSelectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Curricula für Export auswählen</h2>
              <button
                onClick={() => {
                  setShowSelectionModal(false)
                  setSelectedCurricula([])
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  {selectedCurricula.length} von {filteredItems.length} Curricula ausgewählt
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleSelectAll}
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                  >
                    {selectedCurricula.length === filteredItems.length ? 'Alle abwählen' : 'Alle auswählen'}
                  </button>
                </div>
              </div>
              
              <div className="border rounded-lg max-h-96 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Auswahl</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fach</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Prüfungsart</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredItems.map(curriculum => (
                      <tr key={curriculum.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedCurricula.includes(curriculum.id)}
                            onChange={() => handleSelectCurriculum(curriculum.id)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {curriculum.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {curriculum.subject}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {curriculum.exam_type || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="bg-blue-50 p-3 rounded-md">
                <p className="text-sm text-blue-800">
                  <strong>Hinweis:</strong> Nur die ausgewählten Curricula werden exportiert. 
                  Die Export-Datei enthält alle Kompetenzen, Inhaltsfelder und Mappings der ausgewählten Curricula.
                </p>
              </div>
              
              <div className="flex gap-2 pt-4">
                <button
                  onClick={handleExportSelected}
                  disabled={selectedCurricula.length === 0 || isExporting}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isExporting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isExporting ? 'Exportiere...' : `Exportieren (${selectedCurricula.length})`}
                </button>
                <button
                  onClick={() => {
                    setShowSelectionModal(false)
                    setSelectedCurricula([])
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


